import { useCallback, useEffect, useState } from "react";

// One-time discoverability hint for the quick chat feature.
// Persisted in localStorage because this is a UX preference (not incognito
// state) — we want it to survive across tabs and browser restarts so a user
// who has already seen it isn't nagged again.
//
// Two states, on purpose:
// • attentionSeen — once the user opens the sidebar at all, the pulsing
//   yellow dot on the burger menu has done its job. Stop pulsing.
// • seen — the callout inside the sidebar is educational; it stays visible
//   until the user dismisses it (X, "Got it", clicking the pinned row, or
//   closing the sidebar after having opened it). Dismissing also implies
//   attentionSeen.

const ATTENTION_KEY = "ace_quick_chat_attention_seen";
const SEEN_KEY = "ace_quick_chat_intro_seen";
const EVENT = "ace-quick-chat-intro-changed";

const readFlag = (key) => {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const writeFlag = (key) => {
  try {
    localStorage.setItem(key, "true");
  } catch {
    // privacy-mode storage rejection — fall through; in-memory state still works
  }
};

const useQuickChatIntro = () => {
  const [seen, setSeen] = useState(() => readFlag(SEEN_KEY));
  const [attentionSeen, setAttentionSeen] = useState(() => readFlag(ATTENTION_KEY));

  // Sync across hook instances on the page (navbar + sidebar) and tabs.
  useEffect(() => {
    const handler = () => {
      setSeen(readFlag(SEEN_KEY));
      setAttentionSeen(readFlag(ATTENTION_KEY));
    };
    window.addEventListener(EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const notify = () => {
    try {
      window.dispatchEvent(new Event(EVENT));
    } catch {
      // older browsers without Event constructor — nothing to do
    }
  };

  // Stop the buzzer dot. Doesn't dismiss the educational callout.
  const markAttentionSeen = useCallback(() => {
    writeFlag(ATTENTION_KEY);
    setAttentionSeen(true);
    notify();
  }, []);

  // Dismiss everything (X, Got it, pinned row click, sidebar close after open).
  const markSeen = useCallback(() => {
    writeFlag(SEEN_KEY);
    writeFlag(ATTENTION_KEY);
    setSeen(true);
    setAttentionSeen(true);
    notify();
  }, []);

  return { seen, attentionSeen, markSeen, markAttentionSeen };
};

export default useQuickChatIntro;
